/**
 * Custom Pick Adapter
 *
 * Transforms admin preview data into CustomPick component props.
 * CustomPick displays options with optional images (team logo, player image, or custom URL).
 */

import type { PreviewQuestionData } from '../types';
import type { CustomOption } from '@low6-pickem/ui/components/CustomPick';

interface CustomPickProps {
  questionText: string;
  subtext?: string;
  options: CustomOption[];
  selectedOption?: string;
  onOptionSelect: (optionId: string | undefined) => void;
  points: string | number;
  earnedPoints?: number;
  isCorrect?: boolean;
  disabled?: boolean;
  questionId?: string;
  questionNumber?: number;
  questionMetadata?: {
    dailyDouble?: boolean;
  };
}

/**
 * Transforms preview data into CustomPick props
 *
 * Maps options to CustomOption format with id, label, imageUrl, popularityPct, thirdImageUrl.
 *
 * @param data - Preview question data from admin form
 * @returns CustomPick component props
 */
export function customPickAdapter(data: PreviewQuestionData): CustomPickProps {
  const language = data.language || 'en';
  const textField = language === 'en' ? 'option_text_en' : 'option_text_fr';
  const imageField = language === 'en' ? 'option_image_en' : 'option_image_fr';
  const thirdImageField = language === 'en' ? 'third_image_en' : 'third_image_fr';

  const options: CustomOption[] = data.options.map((opt, index) => ({
    id: `option-${index}`,
    label: opt[textField] || opt.option_text_en || opt.option_text_fr || `Option ${index + 1}`,
    imageUrl: opt[imageField] ?? opt.option_image_en ?? opt.option_image_fr ?? null,
    popularityPct: (opt as { popularity_pct?: number }).popularity_pct,
    thirdImageUrl: opt[thirdImageField] ?? opt.third_image_en ?? opt.third_image_fr ?? null,
  }));

  return {
    questionText: data.questionText,
    subtext: data.questionSubtext,
    options,
    selectedOption: undefined,
    onOptionSelect: () => {},
    points: data.totalPoints,
    disabled: true,
    questionId: 'preview',
    questionNumber: undefined,
  };
}
